import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import BackToTop from '@/components/feature/BackToTop';
import faqSections from './faqData';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

/* ────────────── DATA ────────────── */

const impactStats = [
  { value: '4.2M', label: 'Pounds of E-Waste Diverted from Landfills', icon: 'ri-recycle-line' },
  { value: '18,500', label: 'Devices Responsibly Recycled in 2025', icon: 'ri-server-line' },
  { value: '98%', label: 'Material Recovery Rate from Collected E-Waste', icon: 'ri-pie-chart-line' },
  { value: '2,400', label: 'Tons of CO2 Offset Through Recycling', icon: 'ri-leaf-line' },
];

const businessSectors = [
  { name: 'Technology & Startups', desc: 'Office electronics, server decommissions, laptop fleets, monitor arrays, and startup hardware refresh cycles.', icon: 'ri-macbook-line' },
  { name: 'Healthcare & Medical', desc: 'HIPAA-compliant data destruction, medical device recycling, lab equipment, and patient record security.', icon: 'ri-heart-pulse-line' },
  { name: 'Education & Universities', desc: 'Bulk campus cleanouts, classroom technology, lab equipment, and district-wide refresh programs.', icon: 'ri-graduation-cap-line' },
  { name: 'Government & Municipal', desc: 'Secure chain-of-custody, compliance documentation, and regulated electronics disposal for public agencies.', icon: 'ri-government-line' },
  { name: 'Real Estate & Property', desc: 'Tenant cleanouts, building-wide collection events, and coordinated e-waste removal during renovations.', icon: 'ri-building-2-line' },
  { name: 'Financial Services', desc: 'Secure data destruction, compliance reporting, and confidential hardware disposal for banks and firms.', icon: 'ri-bank-line' },
];

const processSteps = [
  { step: '01', title: 'Discovery & Assessment', desc: 'We evaluate your e-waste inventory, understand your compliance requirements, and provide a detailed project plan with transparent pricing.', icon: 'ri-search-line' },
  { step: '02', title: 'Secure Collection', desc: 'Certified technicians arrive with GPS-tracked vehicles, locked containers, and chain-of-custody documentation. On-site inventory verification included.', icon: 'ri-truck-line' },
  { step: '03', title: 'Data Destruction', desc: 'Hard drives are wiped to DoD 5220.22-M standards or physically shredded. You receive a Certificate of Data Destruction with serial numbers within 48 hours.', icon: 'ri-shield-check-line' },
  { step: '04', title: 'Responsible Processing', desc: 'Devices are sorted, dismantled, and sent to specialized material recovery facilities. Hazardous materials handled per EPA and CalRecycle standards.', icon: 'ri-hammer-line' },
  { step: '05', title: 'Certification & Reporting', desc: 'You receive a Certificate of Recycling, itemized weight report, material recovery summary, and carbon impact calculation for ESG reporting.', icon: 'ri-file-list-3-line' },
];

const certifications = [
  { name: 'CalRecycle CEWD-127531', desc: 'California Department of Resources Recycling and Recovery certified collection site and processor.', icon: 'ri-award-line' },
  { name: 'HIPAA Compliant', desc: 'Data destruction processes meet federal requirements for electronic protected health information disposal.', icon: 'ri-shield-keyhole-line' },
  { name: 'DoD 5220.22-M', desc: 'Hard drive wiping meets U.S. Department of Defense standards for data sanitization before recycling.', icon: 'ri-lock-line' },
  { name: 'Chain of Custody', desc: 'Full documentation from pickup through destruction — every device tracked, every step recorded.', icon: 'ri-route-line' },
  { name: 'Zero Landfill Commitment', desc: '100% of materials reused, recycled, or responsibly processed. Nothing enters municipal landfills.', icon: 'ri-recycle-line' },
  { name: 'Certificate of Destruction', desc: 'Physical shredding documented with serial numbers, destruction method, date, and certification details.', icon: 'ri-file-shield-line' },
];

const caseStudies = [
  {
    client: 'LA County School District',
    type: 'Education',
    summary: 'Recycled 3,200 devices across 12 campuses during a district-wide technology refresh.',
    result: '12,400 lbs diverted · 8.2 tons CO2 offset · $0 cost (qualifying volume)',
  },
  {
    client: 'Downtown LA Medical Group',
    type: 'Healthcare',
    summary: 'HIPAA-compliant destruction of 450 hard drives and medical devices during facility relocation.',
    result: '450 certificates issued · 0 data breaches · completed in 2 days',
  },
  {
    client: 'Santa Monica Tech Campus',
    type: 'Technology',
    summary: 'Server decommission and office cleanout for a 200-employee tech company moving to hybrid work.',
    result: '340 servers · 280 monitors · 156 laptops · all reused or recovered',
  },
];

/* ────────────── COMPONENTS ────────────── */

const ReportJsonLd = () => (
  <script type="application/ld+json">
    {JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/sustainability-report#webpage`,
          url: `${SITE_URL}/sustainability-report`,
          name: 'Corporate E-Waste Recycling & Business Sustainability Report | Greenly Los Angeles',
          description: 'Corporate e-waste recycling and business sustainability programs in Los Angeles. ESG-compliant recycling, Certificates of Destruction, HIPAA compliance, and zero-landfill processing. CalRecycle certified CEWD-127531.',
          isPartOf: { '@id': `${SITE_URL}/#website` },
          about: { '@id': `${SITE_URL}/#business` },
          breadcrumb: { '@id': `${SITE_URL}/sustainability-report#breadcrumb` },
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${SITE_URL}/sustainability-report#breadcrumb`,
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
            { '@type': 'ListItem', position: 2, name: 'Sustainability & Corporate Recycling', item: `${SITE_URL}/sustainability-report` },
          ],
        },
        {
          '@type': 'FAQPage',
          '@id': `${SITE_URL}/sustainability-report#faqpage`,
          mainEntity: faqSections.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        },
      ],
    })}
  </script>
);

const SectionHeading = ({ eyebrow, title, subtitle }: { eyebrow?: string; title: string; subtitle?: string }) => (
  <div className="text-center mb-10 md:mb-12">
    {eyebrow && (
      <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-3">
        {eyebrow}
      </div>
    )}
    <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3 leading-tight">
      {title}
    </h2>
    {subtitle && (
      <p className="text-gray-500 text-sm max-w-2xl mx-auto leading-relaxed">
        {subtitle}
      </p>
    )}
  </div>
);

const FAQAccordion = () => {
  const [openId, setOpenId] = useState<string | null>(null);
  const toggle = (id: string) => setOpenId(openId === id ? null : id);

  return (
    <div className="max-w-4xl mx-auto space-y-3">
      {faqSections.map((faq) => {
        const isOpen = openId === faq.id;
        return (
          <div key={faq.id} className="border border-gray-200 rounded-xl overflow-hidden">
            <button
              className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left cursor-pointer hover:bg-[#F4FAF7] transition-colors"
              onClick={() => toggle(faq.id)}
            >
              <span className="text-sm font-bold text-gray-900 leading-snug">{faq.question}</span>
              <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                <i className={`text-[#1B6B3A] text-base transition-transform duration-200 ${isOpen ? 'ri-subtract-line' : 'ri-add-line'}`} />
              </div>
            </button>
            {isOpen && (
              <div className="px-5 pb-5 pt-1 bg-[#F4FAF7]">
                <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ────────────── PAGE ────────────── */

const SustainabilityReportPage = () => {
  return (
    <>
      <title>Corporate E-Waste Recycling &amp; Business Sustainability | Greenly Los Angeles</title>
      <meta name="description" content="Corporate e-waste recycling and business sustainability programs in Los Angeles. ESG-compliant recycling, Certificates of Destruction, HIPAA compliance, and zero-landfill processing. CalRecycle certified CEWD-127531." />
      <meta name="keywords" content="corporate e-waste recycling, business sustainability, ESG recycling, zero landfill electronics, HIPAA compliant data destruction, office electronics recycling Los Angeles, enterprise e-waste disposal, sustainability report, data center decommission, certificate of recycling" />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content="2026-05-11" />
      <link rel="canonical" href={`${SITE_URL}/sustainability-report`} />

      {/* Open Graph */}
      <meta property="og:title" content="Corporate E-Waste Recycling & Business Sustainability | Greenly Los Angeles" />
      <meta property="og:description" content="Corporate e-waste recycling and business sustainability programs in Los Angeles. ESG-compliant recycling with full documentation." />
      <meta property="og:image" content="https://greenlyelectronicsrecycling.com/api/images/greenly.png" />
      <meta property="og:url" content={`${SITE_URL}/sustainability-report`} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Greenly Electronics Recycling" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Corporate E-Waste Recycling & Business Sustainability | Greenly Los Angeles" />
      <meta name="twitter:description" content="Corporate e-waste recycling and business sustainability programs in Los Angeles. ESG-compliant recycling with full documentation." />
      <meta name="twitter:image" content="https://greenlyelectronicsrecycling.com/api/images/greenly.png" />
      <meta name="twitter:site" content="@greenlyrecycling" />

      <ReportJsonLd />
      <Navbar />
      <main>
        {/* ════════════════════════ HERO ════════════════════════ */}
        <section className="bg-[#0F3D22] pt-28 pb-14 px-4 md:px-6 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20sustainable%20business%20campus%20with%20green%20rooftop%20gardens%20and%20solar%20panels%2C%20professional%20corporate%20environment%2C%20clean%20minimal%20architectural%20photography%20with%20emerald%20green%20and%20warm%20earth%20tones%2C%20wide%20angle%20aerial%20view&width=1400&height=400&seq=sustain-hero-bg&orientation=landscape"
              alt="Corporate sustainability and green business"
              className="w-full h-full object-cover object-center"
              loading="eager"
            />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: 'Sustainability & Corporate Recycling' }]} />
            <div className="max-w-3xl mt-4">
              <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4">
                <i className="ri-bar-chart-box-line" />
                ESG &amp; Corporate Programs
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-4">
                Corporate E-Waste Recycling &amp; Business Sustainability
              </h1>
              <p className="text-green-200 text-base leading-relaxed max-w-2xl">
                ESG-compliant electronics recycling for Los Angeles businesses. Full documentation, Certificates of Destruction, HIPAA compliance, and zero-landfill processing. Turn your e-waste into a sustainability win.
              </p>
            </div>
          </div>
        </section>

        {/* ════════════════════════ IMPACT STATS ════════════════════════ */}
        <section className="py-12 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {impactStats.map((stat) => (
                <div key={stat.label} className="bg-[#F4FAF7] border border-green-100 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1B6B3A] rounded-xl mx-auto mb-3">
                    <i className={`${stat.icon} text-white text-lg`} />
                  </div>
                  <div className="text-2xl md:text-3xl font-extrabold text-[#1B6B3A] mb-1">
                    {stat.value}
                  </div>
                  <p className="text-gray-600 text-xs leading-relaxed">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════ CORPORATE SERVICES ════════════════════════ */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
              <div>
                <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-3">
                  Enterprise Solutions
                </div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-5 leading-tight">
                  Corporate E-Waste Recycling Programs
                </h2>
                <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
                  <p>
                    <strong className="text-gray-900">Corporate e-waste recycling</strong> is more than disposal — it is a strategic ESG initiative that protects data, ensures compliance, and demonstrates environmental leadership. Greenly partners with Los Angeles businesses to design custom recycling programs that align with sustainability goals and regulatory requirements.
                  </p>
                  <p>
                    Whether you are refreshing a laptop fleet, decommissioning a data center, relocating an office, or managing a district-wide technology upgrade, we provide end-to-end service with the documentation your compliance and sustainability teams need.
                  </p>
                  <div className="bg-[#F4FAF7] border border-green-100 rounded-xl p-4">
                    <h3 className="font-extrabold text-gray-900 text-sm mb-2">Every Corporate Engagement Includes:</h3>
                    <ul className="space-y-1.5">
                      {[
                        'Free qualifying pickup throughout Los Angeles County',
                        'Dedicated project coordinator for planning and logistics',
                        'On-site inventory and chain-of-custody documentation',
                        'DoD 5220.22-M data wiping or physical hard drive shredding',
                        'Certificate of Recycling with itemized weight report',
                        'Certificate of Data Destruction with serial numbers',
                        'Carbon impact summary for ESG and sustainability reporting',
                        'Volume-based pricing with enterprise discounts',
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2 text-xs text-gray-600">
                          <div className="w-4 h-4 flex items-center justify-center bg-[#1B6B3A] rounded-full shrink-0 mt-0.5">
                            <i className="ri-check-line text-white text-[10px]" />
                          </div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                {[
                  {
                    title: 'Office Refresh & Relocation',
                    desc: 'Recycle desktops, monitors, printers, and peripherals during office moves or workstation upgrades. We coordinate with facilities teams for minimal disruption.',
                    icon: 'ri-building-line',
                  },
                  {
                    title: 'Data Center Decommission',
                    desc: 'Full server, storage array, and network equipment removal with secure data destruction. Chain-of-custody documentation for compliance audits.',
                    icon: 'ri-server-line',
                  },
                  {
                    title: 'Laptop Fleet Recycling',
                    desc: 'Bulk collection of end-of-life laptops with standardized data wiping. Asset tags tracked for IT asset management integration.',
                    icon: 'ri-macbook-line',
                  },
                  {
                    title: 'Secure Data Destruction',
                    desc: 'Physical shredding of hard drives, SSDs, and tapes with Certificates of Destruction. HIPAA, SOX, and PCI-DSS compliant processes.',
                    icon: 'ri-shield-check-line',
                  },
                ].map((service) => (
                  <div key={service.title} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-[#1B6B3A]/30 transition-all duration-200">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-lg shrink-0">
                        <i className={`${service.icon} text-[#1B6B3A] text-lg`} />
                      </div>
                      <div>
                        <h3 className="text-sm font-extrabold text-gray-900 mb-1">{service.title}</h3>
                        <p className="text-gray-500 text-xs leading-relaxed">{service.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ════════════════════════ ESG REPORTING ════════════════════════ */}
        <section className="py-16 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <SectionHeading
              eyebrow="ESG Compliance"
              title="Sustainability Reporting &amp; ESG Documentation"
              subtitle="Investors, regulators, and customers increasingly demand documented environmental responsibility. Greenly provides the standardized reports your ESG team needs."
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                {
                  title: 'Certificate of Recycling',
                  desc: 'Documents every item recycled, total weight, material breakdown, date, and our CalRecycle certification number. Essential for sustainability audits and ESG reporting.',
                  icon: 'ri-file-list-3-line',
                },
                {
                  title: 'Carbon Impact Summary',
                  desc: 'Quantified CO2 offset calculations using EPA WARM methodology. Shows the emissions avoided by recycling instead of landfilling or manufacturing new materials.',
                  icon: 'ri-leaf-line',
                },
                {
                  title: 'Material Recovery Report',
                  desc: 'Breaks down recoverable materials by category: precious metals (gold, silver, copper), rare earth elements, plastics, and glass recovered from your e-waste.',
                  icon: 'ri-pie-chart-line',
                },
                {
                  title: 'Chain of Custody Log',
                  desc: 'Timestamped documentation from pickup through processing. GPS tracking, locked transport, and facility processing records for compliance and insurance.',
                  icon: 'ri-route-line',
                },
                {
                  title: 'Certificate of Destruction',
                  desc: 'For hard drives and storage media. Includes serial numbers, destruction method, date, technician certification, and our CalRecycle license number.',
                  icon: 'ri-file-shield-line',
                },
                {
                  title: 'Quarterly Sustainability Reports',
                  desc: 'Enterprise clients receive aggregated quarterly reports with cumulative diversion metrics, carbon savings, and year-over-year environmental impact comparisons.',
                  icon: 'ri-bar-chart-grouped-line',
                },
              ].map((doc) => (
                <div key={doc.title} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-[#1B6B3A]/30 transition-all duration-200">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-xl mb-3">
                    <i className={`${doc.icon} text-[#1B6B3A] text-lg`} />
                  </div>
                  <h3 className="text-sm font-extrabold text-gray-900 mb-2">{doc.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{doc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════ INDUSTRIES ════════════════════════ */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <SectionHeading
              eyebrow="Industries We Serve"
              title="Business Sectors &amp; Specialized Programs"
              subtitle="We tailor our recycling programs to the unique compliance, security, and operational requirements of each industry."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {businessSectors.map((sector) => (
                <div key={sector.name} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-[#1B6B3A]/30 hover:bg-[#F4FAF7] transition-all duration-200 group">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-xl mb-3 group-hover:bg-[#1B6B3A] transition-colors">
                    <i className={`${sector.icon} text-[#1B6B3A] text-lg group-hover:text-white transition-colors`} />
                  </div>
                  <h3 className="text-sm font-extrabold text-gray-900 mb-2">{sector.name}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{sector.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════ HOW IT WORKS ════════════════════════ */}
        <section className="py-16 bg-[#0F3D22]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-12">
              <div className="text-[#4CAF82] text-xs font-bold uppercase tracking-widest mb-3">Our Process</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 leading-tight">
                How Corporate E-Waste Recycling Works
              </h2>
              <p className="text-green-200 text-sm max-w-2xl mx-auto leading-relaxed">
                A five-step process designed for minimal disruption, maximum security, and complete compliance documentation.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {processSteps.map((item) => (
                <div key={item.step} className="bg-white/10 border border-white/10 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-[#4CAF82] rounded-full text-white text-xs font-extrabold">
                      {item.step}
                    </div>
                    <div className="w-7 h-7 flex items-center justify-center bg-[#4CAF82]/20 rounded-lg">
                      <i className={`${item.icon} text-[#4CAF82] text-sm`} />
                    </div>
                  </div>
                  <h3 className="text-sm font-extrabold text-white mb-2">{item.title}</h3>
                  <p className="text-green-200/80 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════ CERTIFICATIONS ════════════════════════ */}
        <section className="py-16 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <SectionHeading
              eyebrow="Compliance & Trust"
              title="Certifications &amp; Standards"
              subtitle="Greenly meets the highest regulatory and industry standards for electronics recycling and data security."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {certifications.map((cert) => (
                <div key={cert.name} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-[#1B6B3A]/30 transition-all duration-200">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-lg shrink-0">
                      <i className={`${cert.icon} text-[#1B6B3A] text-lg`} />
                    </div>
                    <div>
                      <h3 className="text-sm font-extrabold text-gray-900 mb-1">{cert.name}</h3>
                      <p className="text-gray-500 text-xs leading-relaxed">{cert.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════ CASE STUDIES ════════════════════════ */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <SectionHeading
              eyebrow="Real Results"
              title="Corporate Recycling Case Studies"
              subtitle="Examples of recent engagements showing the scale, scope, and impact of our corporate e-waste programs."
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {caseStudies.map((study) => (
                <div key={study.client} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="bg-[#1B6B3A] px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="bg-white/20 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                        {study.type}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-sm font-extrabold text-gray-900 mb-2">{study.client}</h3>
                    <p className="text-gray-500 text-xs leading-relaxed mb-3">{study.summary}</p>
                    <div className="bg-[#F4FAF7] border border-green-100 rounded-lg p-3">
                      <p className="text-[#1B6B3A] text-xs font-semibold leading-relaxed">{study.result}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════ IMPACT CALCULATOR CTA ════════════════════════ */}
        <section className="py-16 bg-[#F4FAF7]">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-3">Environmental Impact</div>
                  <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4 leading-tight">
                    The Business Case for E-Waste Recycling
                  </h2>
                  <p className="text-gray-600 text-sm leading-relaxed mb-5">
                    Recycling electronics is not just an environmental win — it is a business advantage. Companies with documented sustainability programs outperform peers in employee retention, customer loyalty, and investor confidence. Plus, California law requires proper disposal, and violations carry real penalties.
                  </p>
                  <div className="space-y-3">
                    {[
                      { stat: '70–90%', label: 'Energy savings when recycling vs. manufacturing new materials' },
                      { stat: '200 kg', label: 'CO2 generated by manufacturing one laptop' },
                      { stat: '$62B', label: 'Value of raw materials lost in global e-waste annually' },
                      { stat: '15,000 kg', label: 'Typical CO2 savings from recycling 100 office devices' },
                    ].map((item) => (
                      <div key={item.label} className="flex items-start gap-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-[#E8F5EE] rounded-lg shrink-0">
                          <i className="ri-check-line text-[#1B6B3A] text-sm" />
                        </div>
                        <div>
                          <span className="text-[#1B6B3A] font-extrabold text-sm">{item.stat}</span>
                          <p className="text-gray-500 text-xs">{item.label}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-[#F4FAF7] border border-green-100 rounded-xl p-6">
                  <h3 className="text-lg font-extrabold text-gray-900 mb-3">Estimate Your Impact</h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4">
                    Enter your approximate inventory and we will estimate the environmental impact and documentation you will receive.
                  </p>
                  <div className="space-y-3">
                    {[
                      { label: 'Laptops / Desktops', count: '100 units' },
                      { label: 'Monitors', count: '80 units' },
                      { label: 'Servers / Network Gear', count: '20 units' },
                      { label: 'Printers / Peripherals', count: '40 units' },
                    ].map((line) => (
                      <div key={line.label} className="flex items-center justify-between py-2 border-b border-green-100">
                        <span className="text-gray-700 text-sm">{line.label}</span>
                        <span className="text-[#1B6B3A] font-bold text-sm">{line.count}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-green-200">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-600 text-xs">Estimated weight diverted</span>
                      <span className="text-[#1B6B3A] font-extrabold text-sm">5,800 lbs</span>
                    </div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-600 text-xs">Estimated CO2 offset</span>
                      <span className="text-[#1B6B3A] font-extrabold text-sm">9,200 kg</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 text-xs">Documents provided</span>
                      <span className="text-[#1B6B3A] font-extrabold text-sm">4 certificates</span>
                    </div>
                  </div>
                  <Link
                    to="/pickup-request"
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#1B6B3A] text-white font-bold px-5 py-3 rounded-md hover:bg-[#155530] transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    <i className="ri-truck-line" />
                    Schedule a Corporate Pickup
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ════════════════════════ FAQ ════════════════════════ */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <SectionHeading
              eyebrow="Common Questions"
              title="Corporate E-Waste Recycling FAQ"
              subtitle="Answers for sustainability officers, facilities managers, IT directors, and compliance teams."
            />
            <FAQAccordion />
          </div>
        </section>

        {/* ════════════════════════ RELATED RESOURCES ════════════════════════ */}
        <section className="py-16 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <SectionHeading
              eyebrow="Learn More"
              title="Related Resources"
              subtitle="Explore our guides, services, and educational content for responsible electronics recycling."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                {
                  title: 'E-Waste Guide & Glossary',
                  desc: 'Learn what e-waste is, the 10 types, California laws, and how to recycle responsibly.',
                  link: '/e-waste-guide',
                  icon: 'ri-book-open-line',
                },
                {
                  title: 'Hard Drive Destruction',
                  desc: 'Certified data destruction with Certificates of Destruction. HIPAA-compliant shredding.',
                  link: '/hard-drive-destruction',
                  icon: 'ri-hard-drive-line',
                },
                {
                  title: 'Items We Recycle & FAQ',
                  desc: 'Full list of accepted items and answers to 20+ common e-waste questions.',
                  link: '/faq',
                  icon: 'ri-question-answer-line',
                },
                {
                  title: 'TV Recycling Guide',
                  desc: 'How to recycle CRT, LCD, and LED televisions in Los Angeles.',
                  link: '/tv-recycling',
                  icon: 'ri-tv-line',
                },
                {
                  title: 'Computer Recycling Guide',
                  desc: 'Desktop and laptop recycling in LA with data security best practices.',
                  link: '/computer-recycling',
                  icon: 'ri-computer-line',
                },
                {
                  title: 'Blog & Resources',
                  desc: 'E-waste recycling tips, community event guides, and in-depth articles.',
                  link: '/blog',
                  icon: 'ri-article-line',
                },
              ].map((resource) => (
                <Link
                  key={resource.link}
                  to={resource.link}
                  className="bg-white border border-gray-200 rounded-xl p-5 hover:border-[#1B6B3A]/30 hover:bg-[#F4FAF7] transition-all duration-200 group cursor-pointer"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#E8F5EE] rounded-lg group-hover:bg-[#1B6B3A] transition-colors">
                      <i className={`${resource.icon} text-[#1B6B3A] text-sm group-hover:text-white transition-colors`} />
                    </div>
                    <h3 className="text-sm font-extrabold text-gray-900 group-hover:text-[#1B6B3A] transition-colors">
                      {resource.title}
                    </h3>
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed">{resource.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════ CTA ════════════════════════ */}
        <section className="py-14 bg-[#1B6B3A]">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
              Start Your Corporate Recycling Program
            </h2>
            <p className="text-green-100 text-sm md:text-base mb-8 max-w-xl mx-auto leading-relaxed">
              Free pickup for qualifying loads. Full ESG documentation. HIPAA-compliant data destruction. Serving all of Los Angeles County.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/pickup-request"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#1B6B3A] font-bold px-8 py-3 rounded-md hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-truck-line" />
                Request Free Business Pickup
              </Link>
              <a
                href="tel:+12136001000"
                className="inline-flex items-center justify-center gap-2 bg-white/20 border border-white/40 text-white font-semibold px-8 py-3 rounded-md hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-phone-line" />
                Call (213) 600-1000
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
};

export default SustainabilityReportPage;